import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../api';
import {
  beginFile, drop, enqueue, fail, fileDone, nextDraft, progress, restore, retry,
  type Draft, type DraftFile,
} from './outbox';
import type { QueueStore } from './deviceStore';
import { sendDraft } from './sendDraft';

/*
 * Состояние очереди отправки и её ход. Живёт в ChatProvider, над всеми
 * разделами: уход в другой чат, в календарь или в звонок загрузку не рвёт.
 * Очередь копируется на устройство (решение 04.11.2026): после перезапуска
 * непосланное возвращается как «не отправлено». Пишем при каждом структурном
 * изменении, но не на каждый процент хода.
 */

export type EnqueueInput = {
  channelId: string;
  parentId: number | null;
  text: string;
  files: File[];
  replyToId?: number;
  /** Быстрый путь в открытую ленту; то же сообщение приезжает и потоком. */
  onSent?: (m: ChatMessage) => void;
  /** Голосовое: длительность и форма волны сняты при записи, не из файла. */
  voice?: { seconds: number; waveform: number[] };
};

/*
 * Ключ черновика уходит на сервер как clientKey и должен быть уникален и
 * после перезапуска, поэтому счётчика мало — нужен случайный.
 */
const randomKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
const nextKey = randomKey;

/** Подпись одного черновика: меняется на всё, кроме хода в процентах. */
const shapeOf = (d: Draft): string =>
  `${d.state}:${d.fileIndex}:${d.uploaded.join(',')}:${d.error}`;
/** Подпись очереди: набор ключей и подписи черновиков. */
const shape = (q: Draft[]): string => q.map((d) => `${d.key}:${shapeOf(d)}`).join('|');

const toDraftFile = (file: File): DraftFile => ({
  key: nextKey(),
  file,
  name: file.name,
  mime: file.type,
  size: file.size,
  localUrl: URL.createObjectURL(file),
});

export function useOutbox(userId: string, store: QueueStore) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  /*
   * Чья это очередь. Сменился человек в той же вкладке — прежняя очередь
   * уходит из памяти сразу, в том же показе: чужие черновики не мелькнут.
   */
  const [owner, setOwner] = useState(userId);
  /** Очередь этого человека с устройства уже прочитана: до того писать нельзя, затрём. */
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  if (owner !== userId) {
    setOwner(userId);
    setLoadedFor(null);
    drafts.forEach((d) => d.files.forEach((f) => URL.revokeObjectURL(f.localUrl)));
    setDrafts([]);
  }
  const latest = useRef(drafts);
  latest.current = drafts;
  /* Функции в очередь не кладём: она должна остаться простыми данными. */
  const callbacks = useRef(new Map<string, (m: ChatMessage) => void>());
  const busy = useRef(false);
  /*
   * Номера сообщений, доставленных очередью. Черновик уже проиграл движение
   * появления сам — настоящему сообщению, что приезжает ему на замену через
   * onSent или поток, повторно играть его не нужно (находка 2). Набор растил
   * бы память вечно, поэтому clearSettled чистит его при смене переписки —
   * тем же движением, что и свой набор новых строк чистит useFreshRows.
   */
  const [settledIds, setSettledIds] = useState<ReadonlySet<number>>(() => new Set());
  const clearSettled = useCallback(() => setSettledIds(new Set()), []);

  /* Что этот экземпляр уже записал: ключ черновика -> подпись. Чужое (другой вкладки) не трогаем. */
  const persisted = useRef(new Map<string, string>());

  useEffect(() => {
    let alive = true;
    persisted.current = new Map();
    void store.load(userId).then((saved) => {
      if (!alive) return;
      saved.forEach((d) => persisted.current.set(d.key, shapeOf(d)));
      // то, что человек успел поставить, пока читалось, остаётся после старого
      setDrafts((q) => {
        const have = new Set(q.map((d) => d.key));
        // из базы записи приходят в порядке случайных ключей — возвращаем порядок отправки
        const ordered = saved.filter((d) => !have.has(d.key)).sort((a, b) => a.ts - b.ts);
        return [...restore(ordered), ...q];
      });
      setLoadedFor(userId);
    });
    return () => {
      alive = false;
    };
  }, [userId, store]);

  const signature = shape(drafts);
  useEffect(() => {
    if (loadedFor !== userId) return;
    // пишем только изменившиеся черновики и стираем только те, что ушли отсюда
    const known = persisted.current;
    const now = new Set<string>();
    for (const d of latest.current) {
      now.add(d.key);
      const sh = shapeOf(d);
      if (known.get(d.key) !== sh) {
        known.set(d.key, sh);
        void store.put(userId, d).catch(() => {});
      }
    }
    for (const key of [...known.keys()]) {
      if (now.has(key)) continue;
      known.delete(key);
      void store.remove(userId, key).catch(() => {});
    }
    // signature, а не drafts: проценты хода записи не повод
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, loadedFor, userId, store]);

  const enqueueDraft = useCallback((input: EnqueueInput) => {
    const key = nextKey();
    if (input.onSent) callbacks.current.set(key, input.onSent);
    setDrafts((q) =>
      enqueue(q, {
        key,
        channelId: input.channelId,
        parentId: input.parentId,
        replyToId: input.replyToId,
        text: input.text,
        files: input.files.map(toDraftFile),
        ts: Date.now(),
        state: 'ждёт',
        fileIndex: 0,
        pct: 0,
        uploaded: [],
        error: '',
        voice: input.voice,
      }),
    );
  }, []);

  const retryDraft = useCallback((key: string) => setDrafts((q) => retry(q, key)), []);

  const dropDraft = useCallback((key: string) => {
    const draft = drafts.find((d) => d.key === key);
    // Если черновик в состоянии 'идёт', не трогаем его: отправка уже идёт,
    // и убирать его из ленты, пока сеть не завершила передачу, хуже, чем оставить.
    if (draft?.state === 'идёт') return;

    const toRevoke = draft?.files ?? [];
    setDrafts((q) => drop(q, key));
    toRevoke.forEach((f) => URL.revokeObjectURL(f.localUrl));
    callbacks.current.delete(key);
  }, [drafts]);

  useEffect(() => {
    if (busy.current) return;
    const draft = nextDraft(drafts);
    if (!draft) return;
    busy.current = true;
    void (async () => {
      try {
        const message = await sendDraft(draft, {
          begin: (i) => setDrafts((q) => beginFile(q, draft.key, i)),
          progress: (pct) => setDrafts((q) => progress(q, draft.key, pct)),
          fileDone: (id) => setDrafts((q) => fileDone(q, draft.key, id)),
        });
        draft.files.forEach((f) => URL.revokeObjectURL(f.localUrl));
        setDrafts((q) => drop(q, draft.key));
        setSettledIds((prev) => new Set(prev).add(message.id));
        callbacks.current.get(draft.key)?.(message);
        callbacks.current.delete(draft.key);
      } catch (e) {
        setDrafts((q) => fail(q, draft.key, e instanceof Error ? e.message : 'Не удалось отправить'));
      } finally {
        busy.current = false;
      }
    })();
  }, [drafts]);

  return { drafts, enqueueDraft, retryDraft, dropDraft, settledIds, clearSettled };
}
